import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'

/**
 * NewEmailTypePage
 *
 * Full page for creating a new Automated Email (EmailType).
 * Replaces the previous modal with a dedicated page that has
 * breadcrumb navigation and more room for delivery settings.
 */

const TRIGGER_EVENTS = [
  { value: 'event_promotion_completed', label: 'Event Promotion Completed' },
  { value: 'company_created', label: 'Company Created' },
  { value: 'event_published', label: 'Event Published' },
]

const NewEmailTypePage = () => {
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [key, setKey] = useState('')
  const [keyManuallyEdited, setKeyManuallyEdited] = useState(false)
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Delivery settings
  const [showDeliverySettings, setShowDeliverySettings] = useState(false)
  const [triggerType, setTriggerType] = useState('')
  const [triggerEvent, setTriggerEvent] = useState('')
  const [scopeType, setScopeType] = useState('event_company')
  const [scopeFilter, setScopeFilter] = useState('')
  const [subjectTemplate, setSubjectTemplate] = useState('')
  const [recipientType, setRecipientType] = useState('company_emails')
  const [fromName, setFromName] = useState('Event Vesta')
  const [fromEmail, setFromEmail] = useState('info@eventvesta.com')

  // Track if form has been touched for unsaved changes prompt
  const [isDirty, setIsDirty] = useState(false)

  // Auto-generate key from name (unless manually edited)
  useEffect(() => {
    if (!keyManuallyEdited) {
      const generatedKey = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '')
      setKey(generatedKey)
    }
  }, [name, keyManuallyEdited])

  const handleFieldChange = (setter) => (e) => {
    setter(e.target.value)
    setIsDirty(true)
  }

  const handleKeyChange = (e) => {
    setKey(e.target.value)
    setKeyManuallyEdited(true)
    setIsDirty(true)
  }

  const handleBack = (e) => {
    if (isDirty) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        e.preventDefault()
        return
      }
    }
    navigate('/admin/automated_emails')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const emailTypeData = {
        name,
        key,
        description,
      }

      // Include delivery settings if configured
      if (triggerType) {
        emailTypeData.trigger_type = triggerType
        if (triggerType === 'event_driven') {
          emailTypeData.trigger_event = triggerEvent
        }
        emailTypeData.scope_type = scopeType
        if (scopeType === 'companies_with_tag') {
          emailTypeData.scope_filter = scopeFilter
        }
        emailTypeData.recipient_type = recipientType
        emailTypeData.from_name = fromName
        emailTypeData.from_email = fromEmail
      }

      if (subjectTemplate) {
        emailTypeData.subject_template = subjectTemplate
      }

      const response = await fetch('/admin/email_types', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ email_type: emailTypeData })
      })

      if (response.ok) {
        navigate('/admin/automated_emails')
      } else {
        const data = await response.json()
        setError(data.errors?.join(', ') || 'Failed to create email type')
      }
    } catch (err) {
      console.error('Error creating email type:', err)
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="email-templates-page">
      <div className="page-container">
        {/* Breadcrumbs */}
        <nav className="breadcrumbs">
          <Link to="/admin/automated_emails" className="breadcrumb-link" onClick={handleBack}>
            Automated Emails
          </Link>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">New Automated Email</span>
        </nav>

        <header className="page-header">
          <div className="header-left">
            <h1 className="page-title">New Automated Email</h1>
            <p className="page-subtitle">
              Create a new email type and configure how it gets delivered
            </p>
          </div>
        </header>

        <div className="form-page-card">
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="form-error-alert">
                <i className="fa fa-exclamation-circle"></i>
                {error}
              </div>
            )}

            <div className="form-section">
              <div className="form-field">
                <label htmlFor="email-type-name">
                  Name <span className="field-required">*</span>
                </label>
                <input
                  id="email-type-name"
                  type="text"
                  className="field-input"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setIsDirty(true) }}
                  placeholder="e.g., Monthly Newsletter"
                  required
                  autoFocus
                />
              </div>

              <div className="form-field">
                <label htmlFor="email-type-key">
                  Key <span className="field-hint">(auto-generated)</span>
                </label>
                <input
                  id="email-type-key"
                  type="text"
                  className="field-input field-input--mono"
                  value={key}
                  onChange={handleKeyChange}
                  placeholder="e.g., monthly_newsletter"
                />
                <p className="field-description">
                  Unique identifier used in code. Auto-generated from name, but you can customize it.
                </p>
              </div>

              <div className="form-field">
                <label htmlFor="email-type-description">Description</label>
                <textarea
                  id="email-type-description"
                  className="field-input"
                  value={description}
                  onChange={handleFieldChange(setDescription)}
                  placeholder="Brief description of when this email is sent..."
                  rows={3}
                />
              </div>
            </div>

            {/* Delivery Settings */}
            <div className="form-section-divider">
              <button
                type="button"
                className="section-toggle"
                onClick={() => setShowDeliverySettings(!showDeliverySettings)}
              >
                <i className={`fa fa-chevron-${showDeliverySettings ? 'down' : 'right'}`}></i>
                Delivery Settings
                <span className="field-hint">(optional)</span>
              </button>
            </div>

            {showDeliverySettings && (
              <div className="form-section form-section--inset">
                <div className="form-field">
                  <label htmlFor="trigger-type">Trigger Type</label>
                  <select
                    id="trigger-type"
                    className="field-input"
                    value={triggerType}
                    onChange={handleFieldChange(setTriggerType)}
                  >
                    <option value="">None (manual only)</option>
                    <option value="event_driven">Event-Driven</option>
                    <option value="scheduled_daily">Daily Schedule</option>
                    <option value="scheduled_monthly">Monthly Schedule</option>
                  </select>
                </div>

                {triggerType === 'event_driven' && (
                  <div className="form-field">
                    <label htmlFor="trigger-event">
                      Trigger Event <span className="field-required">*</span>
                    </label>
                    <select
                      id="trigger-event"
                      className="field-input"
                      value={triggerEvent}
                      onChange={handleFieldChange(setTriggerEvent)}
                      required
                    >
                      <option value="">Select an event...</option>
                      {TRIGGER_EVENTS.map(evt => (
                        <option key={evt.value} value={evt.value}>{evt.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-field">
                  <label>Recipient Scope</label>
                  <div className="radio-options">
                    {triggerType === 'event_driven' && (
                      <label className="radio-option">
                        <input
                          type="radio"
                          name="scope_type"
                          value="event_company"
                          checked={scopeType === 'event_company'}
                          onChange={handleFieldChange(setScopeType)}
                        />
                        Company of the triggering event
                      </label>
                    )}
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="scope_type"
                        value="all_active_companies"
                        checked={scopeType === 'all_active_companies'}
                        onChange={handleFieldChange(setScopeType)}
                      />
                      All active companies
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="scope_type"
                        value="companies_with_tag"
                        checked={scopeType === 'companies_with_tag'}
                        onChange={handleFieldChange(setScopeType)}
                      />
                      Companies with analytics tag
                    </label>
                  </div>
                </div>

                {scopeType === 'companies_with_tag' && (
                  <div className="form-field">
                    <label htmlFor="scope-filter">
                      Analytics Tag Slug <span className="field-required">*</span>
                    </label>
                    <input
                      id="scope-filter"
                      type="text"
                      className="field-input field-input--mono"
                      value={scopeFilter}
                      onChange={handleFieldChange(setScopeFilter)}
                      placeholder="e.g., referral-reporting"
                      required
                    />
                  </div>
                )}

                <div className="form-field">
                  <label htmlFor="subject-template">Subject Line</label>
                  <input
                    id="subject-template"
                    type="text"
                    className="field-input"
                    value={subjectTemplate}
                    onChange={handleFieldChange(setSubjectTemplate)}
                    placeholder='e.g., Your {{ event.name }} has been promoted!'
                  />
                  <p className="field-description">
                    Supports merge tags like {'{{ event.name }}'} and {'{{ company.name }}'}.
                  </p>
                </div>

                <div className="form-field">
                  <label>Recipient Type</label>
                  <div className="radio-options">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="recipient_type"
                        value="company_emails"
                        checked={recipientType === 'company_emails'}
                        onChange={handleFieldChange(setRecipientType)}
                      />
                      Company communication emails
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="recipient_type"
                        value="owner_email"
                        checked={recipientType === 'owner_email'}
                        onChange={handleFieldChange(setRecipientType)}
                      />
                      Owner email only
                    </label>
                  </div>
                </div>

                <div className="form-field-row">
                  <div className="form-field">
                    <label htmlFor="from-name">From Name</label>
                    <input
                      id="from-name"
                      type="text"
                      className="field-input"
                      value={fromName}
                      onChange={handleFieldChange(setFromName)}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="from-email">From Email</label>
                    <input
                      id="from-email"
                      type="email"
                      className="field-input"
                      value={fromEmail}
                      onChange={handleFieldChange(setFromEmail)}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="form-actions">
              <button
                type="button"
                className="btn-back"
                onClick={handleBack}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={loading || !name.trim() || !key.trim()}
              >
                {loading ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i>
                    Creating...
                  </>
                ) : (
                  'Create Email'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default NewEmailTypePage
